import { Box } from "@mui/material";
import type { FC, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { SURFACE } from "../../../../theme/tokens";
import { EditDepartmentLocationConfigMode } from "../../../../utils/models";
import { V1_CTA_BUTTON_WIDTH, V1_SCROLL_CONTAINER_PADDING_PX, type V1OnlyLocationEditorDesign } from "./consts";

type Props = {
	/** v1 only until its first v2 consumer (the Locations page's expanded editor, APP-780 PR4) brings the v2 markup. */
	design: V1OnlyLocationEditorDesign;
	mode: EditDepartmentLocationConfigMode;
	onCancel: () => void;
	/** Save in Edit mode, Create in Create mode. */
	onSubmit: () => void;
	/** While a save or a delete is running. */
	cancelDisabled: boolean;
	/** Validation, a delete running or awaiting its confirm, or an account that can't edit the template. */
	submitDisabled: boolean;
	submitting: boolean;
	/** Why Submit is disabled, shown on hover. */
	submitTooltip: ReactNode;
};

/** Cancel and Save (or Create), pinned to the foot of the form. */
export const LocationEditorActions: FC<Props> = ({
	design,
	mode,
	onCancel,
	onSubmit,
	cancelDisabled,
	submitDisabled,
	submitting,
	submitTooltip,
}): ReactElement => {
	const { t } = useTranslation();
	switch (design) {
		case "v1":
			return (
				<Box
					sx={{
						display: "flex",
						gap: 1,
						width: "100%",
						justifyContent: "end",
						// Pinned to the foot of whatever scrolls this form. In a popover short enough to be
						// clamped (see `fitViewport`), the actions would otherwise sit below the fold with
						// nothing to suggest a scroll would reach them. `sticky` is inert when the container
						// doesn't scroll, so the unclamped surfaces are unaffected.
						position: "sticky",
						// Negative, by exactly the container's bottom padding: a sticky element parks against
						// the scrollport's CONTENT edge, leaving that padding strip as scrollable area where
						// the text passing underneath would still show. A margin cannot move a stuck element,
						// so the offset itself has to reach into the strip — and `pb` keeps the buttons off it.
						bottom: `-${V1_SCROLL_CONTAINER_PADDING_PX}px`,
						zIndex: 1,
						backgroundColor: SURFACE,
						pt: 1,
						pb: 1,
					}}
				>
					<SystemButton
						sx={{
							width: V1_CTA_BUTTON_WIDTH,
						}}
						buttonVariant={"secondary"}
						onClick={(_) => onCancel()}
						disabled={cancelDisabled}
					>
						{t("cancel")}
					</SystemButton>

					<SystemButton
						data-testid="edit-location-config-submit-button"
						sx={{
							width: V1_CTA_BUTTON_WIDTH,
						}}
						onClick={onSubmit}
						disabled={submitDisabled}
						loading={submitting}
						tooltip={submitTooltip}
					>
						{mode === EditDepartmentLocationConfigMode.Create ? t("schedule.template.v3.editor.create") : t("schedule.template.v3.editor.save")}
					</SystemButton>
				</Box>
			);
	}
};
